import React, { useState } from 'react';
import {
  Link, Settings, Activity, List, Shield, CheckCircle,
  AlertCircle, RefreshCw, ExternalLink, Plus, Trash2, Eye, EyeOff
} from 'lucide-react';
import './CommonStyles.css';

const MOCK_ACCOUNTS = [
  { id: 1, name: 'Business Checking', bank: 'Chase Bank', mask: '4821', type: 'checking', balance: 24350.80, status: 'connected', linked: '2026-03-10' },
  { id: 2, name: 'Savings Account', bank: 'Wells Fargo', mask: '9034', type: 'savings', balance: 8900.00, status: 'connected', linked: '2026-03-15' },
];

const MOCK_TRANSACTIONS = [
  { id: 1, date: '2026-04-24', name: 'Supplier Payment - ABC Corp', amount: -1250.00, account: 'Chase ...4821', category: 'Transfer', status: 'posted' },
  { id: 2, date: '2026-04-23', name: 'Customer Deposit', amount: 4800.00, account: 'Chase ...4821', category: 'Deposit', status: 'posted' },
  { id: 3, date: '2026-04-22', name: 'Payroll - April W2', amount: -6200.00, account: 'Wells Fargo ...9034', category: 'Payroll', status: 'posted' },
  { id: 4, date: '2026-04-21', name: 'Vendor Invoice #INV-991', amount: -340.50, account: 'Chase ...4821', category: 'Payment', status: 'pending' },
];

const MOCK_PAYMENTS = [
  { id: 1, date: '2026-04-24', customer: 'Walk-In Customer', amount: 520.00, method: 'Pay by Bank', account: 'Chase ...4821', status: 'success' },
  { id: 2, date: '2026-04-23', customer: 'Shahabaj', amount: 1200.00, method: 'Pay by Bank', account: 'Wells Fargo ...9034', status: 'success' },
  { id: 3, date: '2026-04-22', customer: 'John Doe', amount: 75.00, method: 'Pay by Bank', account: 'Chase ...4821', status: 'pending' },
];

const MOCK_LOGS = [
  { id: 1, event: 'Account Linked', account: 'Chase ...4821', status: 'Success', message: 'Bank account linked successfully via Plaid Link', date: '2026-03-10 09:12:34' },
  { id: 2, event: 'Balance Sync', account: 'All Accounts', status: 'Success', message: 'Balances refreshed for 2 accounts', date: '2026-04-24 08:00:00' },
  { id: 3, event: 'Payment Initiated', account: 'Chase ...4821', status: 'Success', message: 'ACH transfer of $520.00 initiated', date: '2026-04-24 14:33:10' },
  { id: 4, event: 'Token Refresh', account: 'Wells Fargo ...9034', status: 'Error', message: 'Access token expired — re-authentication required', date: '2026-04-20 11:45:22' },
];

const badge = (status) => {
  const styles = {
    success: { background: '#ecfdf5', color: '#10b981' },
    posted: { background: '#ecfdf5', color: '#10b981' },
    connected: { background: '#eff6ff', color: '#3b82f6' },
    pending: { background: '#fffbeb', color: '#f59e0b' },
    Error: { background: '#fef2f2', color: '#ef4444' },
  };
  const s = styles[status] || { background: '#f1f5f9', color: '#475569' };
  return (
    <span style={{ padding: '0.2rem 0.6rem', borderRadius: 12, fontSize: '0.75rem', fontWeight: 700, ...s }}>
      {status}
    </span>
  );
};

const PlaidConnector = () => {
  const [activeTab, setActiveTab] = useState('setup');
  const [showSecret, setShowSecret] = useState(false);
  const [env, setEnv] = useState('sandbox');
  const [clientId, setClientId] = useState('');
  const [secret, setSecret] = useState('');
  const [syncing, setSyncing] = useState(false);
  const [accounts, setAccounts] = useState(MOCK_ACCOUNTS);

  const handleSync = () => {
    setSyncing(true);
    setTimeout(() => setSyncing(false), 2000);
  };

  const handleRemoveAccount = (id) => setAccounts(a => a.filter(x => x.id !== id));

  const simulateLinkBank = () => {
    alert('In production, this opens the Plaid Link widget so your customer can securely log in to their bank.');
  };

  const tabs = [
    { id: 'setup', label: 'API Setup', icon: <Settings size={14} /> },
    { id: 'accounts', label: 'Linked Accounts', icon: <Link size={14} /> },
    { id: 'payments', label: 'Payments', icon: <Shield size={14} /> },
    { id: 'transactions', label: 'Transactions', icon: <Activity size={14} /> },
    { id: 'logs', label: 'Logs', icon: <List size={14} /> },
  ];

  /* ── Setup Tab ─────────────────────────────────────────────── */
  const renderSetup = () => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
      {/* Info Banner */}
      <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 10, padding: '1rem 1.25rem', display: 'flex', gap: '0.75rem', alignItems: 'flex-start' }}>
        <Shield size={18} color="#3b82f6" style={{ flexShrink: 0, marginTop: 2 }} />
        <div>
          <p style={{ margin: 0, fontWeight: 700, color: '#1e40af', fontSize: '0.9rem' }}>US-Only Integration</p>
          <p style={{ margin: '0.25rem 0 0', fontSize: '0.82rem', color: '#3b82f6' }}>
            Plaid supports 12,000+ US banks. Get your credentials from{' '}
            <a href="https://dashboard.plaid.com" target="_blank" rel="noreferrer" style={{ color: '#3b82f6', fontWeight: 700 }}>dashboard.plaid.com</a>
          </p>
        </div>
      </div>

      {/* Credentials */}
      <div style={{ background: 'var(--etpos-card)', borderRadius: 10, border: '1px solid var(--etpos-border)', padding: '2rem' }}>
        <h3 style={{ margin: '0 0 1.5rem', display: 'flex', alignItems: 'center', gap: '0.75rem', fontSize: '1.1rem' }}>
          <Settings size={18} color="var(--etpos-primary)" /> API Credentials
        </h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1.25rem' }}>
          <div>
            <label style={{ display: 'block', fontWeight: 700, marginBottom: '0.4rem', fontSize: '0.875rem' }}>Environment</label>
            <select value={env} onChange={e => setEnv(e.target.value)}
              style={{ width: '100%', padding: '0.7rem', border: '1px solid var(--etpos-border)', borderRadius: 8, fontSize: '0.9rem', background: 'var(--etpos-card)', color: 'var(--etpos-text)' }}>
              <option value="sandbox">Sandbox (Testing)</option>
              <option value="development">Development</option>
              <option value="production">Production</option>
            </select>
          </div>
          <div>
            <label style={{ display: 'block', fontWeight: 700, marginBottom: '0.4rem', fontSize: '0.875rem' }}>Client ID *</label>
            <input type="text" placeholder="5f3c2b1a0d9e8f7g6h5i4j" value={clientId} onChange={e => setClientId(e.target.value)}
              style={{ width: '100%', padding: '0.7rem', border: '1px solid var(--etpos-border)', borderRadius: 8, fontSize: '0.9rem', boxSizing: 'border-box' }} />
          </div>
          <div>
            <label style={{ display: 'block', fontWeight: 700, marginBottom: '0.4rem', fontSize: '0.875rem' }}>Secret Key *</label>
            <div style={{ position: 'relative' }}>
              <input type={showSecret ? 'text' : 'password'} placeholder="••••••••••••••••••••••" value={secret} onChange={e => setSecret(e.target.value)}
                style={{ width: '100%', padding: '0.7rem 2.5rem 0.7rem 0.7rem', border: '1px solid var(--etpos-border)', borderRadius: 8, fontSize: '0.9rem', boxSizing: 'border-box' }} />
              <button onClick={() => setShowSecret(!showSecret)}
                style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--etpos-text-muted)' }}>
                {showSecret ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Webhook */}
      <div style={{ background: 'var(--etpos-card)', borderRadius: 10, border: '1px solid var(--etpos-border)', padding: '2rem' }}>
        <h3 style={{ margin: '0 0 1.5rem', display: 'flex', alignItems: 'center', gap: '0.75rem', fontSize: '1.1rem' }}>
          <Activity size={18} color="#10b981" /> Webhook Configuration
        </h3>
        <div>
          <label style={{ display: 'block', fontWeight: 700, marginBottom: '0.4rem', fontSize: '0.875rem' }}>Webhook URL (auto-generated)</label>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <input type="text" readOnly value="https://yourstore.com/api/plaid/webhook"
              style={{ flex: 1, padding: '0.7rem', border: '1px solid var(--etpos-border)', borderRadius: 8, fontSize: '0.875rem', background: 'var(--etpos-bg)' }} />
            <button className="export-btn" onClick={() => navigator.clipboard?.writeText('https://yourstore.com/api/plaid/webhook')}>Copy</button>
          </div>
          <p style={{ margin: '0.5rem 0 0', fontSize: '0.8rem', color: 'var(--etpos-text-muted)' }}>Paste this URL in your Plaid Dashboard → Webhooks.</p>
        </div>
      </div>

      <div style={{ display: 'flex', justifyContent: 'center', gap: '1rem' }}>
        <button className="export-btn" style={{ padding: '0.75rem 2rem' }}>Test Connection</button>
        <button className="add-btn" style={{ padding: '0.75rem 3rem' }}>Save Settings</button>
      </div>
    </div>
  );

  /* ── Linked Accounts Tab ───────────────────────────────────── */
  const renderAccounts = () => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(270px, 1fr))', gap: '1.25rem' }}>
        {accounts.map(acc => (
          <div key={acc.id} style={{ background: 'var(--etpos-card)', borderRadius: 12, border: '1px solid var(--etpos-border)', padding: '1.5rem', position: 'relative' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <div style={{ width: 42, height: 42, background: '#eff6ff', borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.25rem' }}>🏦</div>
                <div>
                  <p style={{ margin: 0, fontWeight: 700, fontSize: '0.95rem' }}>{acc.bank}</p>
                  <p style={{ margin: 0, fontSize: '0.8rem', color: 'var(--etpos-text-muted)' }}>•••• {acc.mask}</p>
                </div>
              </div>
              {badge(acc.status)}
            </div>
            <p style={{ margin: '0 0 0.25rem', fontSize: '0.82rem', color: 'var(--etpos-text-muted)' }}>{acc.name} · {acc.type}</p>
            <p style={{ margin: '0 0 1rem', fontSize: '1.4rem', fontWeight: 800, color: '#3b82f6' }}>${acc.balance.toLocaleString('en-US', { minimumFractionDigits: 2 })}</p>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button className="export-btn" style={{ flex: 1, justifyContent: 'center' }} onClick={handleSync}>
                <RefreshCw size={13} className={syncing ? 'spin-animation' : ''} /> Sync
              </button>
              <button onClick={() => handleRemoveAccount(acc.id)}
                style={{ padding: '0.4rem 0.75rem', border: '1px solid #fecaca', borderRadius: 6, background: '#fff', color: '#ef4444', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.3rem', fontSize: '0.82rem' }}>
                <Trash2 size={13} /> Remove
              </button>
            </div>
          </div>
        ))}

        {/* Add New Account Card */}
        <div onClick={simulateLinkBank}
          style={{ background: 'var(--etpos-bg)', borderRadius: 12, border: '2px dashed var(--etpos-border)', padding: '1.5rem', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '0.75rem', cursor: 'pointer', minHeight: 180, transition: 'all 0.2s' }}
          onMouseEnter={e => e.currentTarget.style.borderColor = '#3b82f6'}
          onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--etpos-border)'}>
          <div style={{ width: 48, height: 48, background: '#eff6ff', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Plus size={22} color="#3b82f6" />
          </div>
          <p style={{ margin: 0, fontWeight: 700, color: '#3b82f6', fontSize: '0.95rem' }}>Link New Bank Account</p>
          <p style={{ margin: 0, fontSize: '0.8rem', color: 'var(--etpos-text-muted)', textAlign: 'center' }}>Opens Plaid Link — secure & encrypted</p>
        </div>
      </div>

      {/* Stats Row */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '1rem' }}>
        {[
          { label: 'Linked Banks', value: accounts.length, icon: '🏦', color: '#3b82f6' },
          { label: 'Total Balance', value: `$${accounts.reduce((s, a) => s + a.balance, 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}`, icon: '💰', color: '#10b981' },
          { label: 'Transactions', value: MOCK_TRANSACTIONS.length, icon: '📄', color: '#f59e0b' },
        ].map((s, i) => (
          <div key={i} style={{ background: 'var(--etpos-card)', borderRadius: 10, border: '1px solid var(--etpos-border)', padding: '1.25rem', textAlign: 'center' }}>
            <div style={{ fontSize: '1.5rem', marginBottom: '0.5rem' }}>{s.icon}</div>
            <p style={{ margin: 0, fontWeight: 800, fontSize: '1.3rem', color: s.color }}>{s.value}</p>
            <p style={{ margin: 0, fontSize: '0.8rem', color: 'var(--etpos-text-muted)' }}>{s.label}</p>
          </div>
        ))}
      </div>
    </div>
  );

  /* ── Payments Tab ──────────────────────────────────────────── */
  const renderPayments = () => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
      {/* Info */}
      <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 10, padding: '1rem 1.25rem', display: 'flex', gap: '0.75rem' }}>
        <CheckCircle size={18} color="#10b981" style={{ flexShrink: 0, marginTop: 2 }} />
        <div>
          <p style={{ margin: 0, fontWeight: 700, color: '#166534', fontSize: '0.9rem' }}>Pay by Bank — Lower Fees</p>
          <p style={{ margin: '0.25rem 0 0', fontSize: '0.82rem', color: '#16a34a' }}>
            Bank-to-bank (ACH) payments typically cost 0.5–1% vs 2–3% for card networks. Funds settle in 1–3 business days.
          </p>
        </div>
      </div>

      {/* Payments Table */}
      <div style={{ background: 'var(--etpos-card)', borderRadius: 10, border: '1px solid var(--etpos-border)', overflow: 'hidden' }}>
        <div style={{ padding: '1.25rem 1.5rem', borderBottom: '1px solid var(--etpos-border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3 style={{ margin: 0, fontSize: '1.05rem', fontWeight: 700 }}>Payment History</h3>
          <button className="add-btn" style={{ padding: '0.55rem 1.2rem', fontSize: '0.875rem' }} onClick={simulateLinkBank}>
            <Plus size={14} /> New Payment
          </button>
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table className="products-table" style={{ minWidth: 700 }}>
            <thead>
              <tr>
                <th>DATE</th><th>CUSTOMER</th><th>AMOUNT</th><th>METHOD</th><th>BANK ACCOUNT</th><th>STATUS</th>
              </tr>
            </thead>
            <tbody>
              {MOCK_PAYMENTS.map(p => (
                <tr key={p.id}>
                  <td>{p.date}</td>
                  <td style={{ fontWeight: 600 }}>{p.customer}</td>
                  <td style={{ fontWeight: 700, color: '#10b981' }}>${p.amount.toFixed(2)}</td>
                  <td>{p.method}</td>
                  <td>{p.account}</td>
                  <td>{badge(p.status)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );

  /* ── Transactions Tab ──────────────────────────────────────── */
  const renderTransactions = () => (
    <div style={{ background: 'var(--etpos-card)', borderRadius: 10, border: '1px solid var(--etpos-border)', overflow: 'hidden' }}>
      <div style={{ padding: '1.25rem 1.5rem', borderBottom: '1px solid var(--etpos-border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h3 style={{ margin: 0, fontSize: '1.05rem', fontWeight: 700 }}>Bank Transactions (Auto-Reconciliation)</h3>
        <button className="export-btn" onClick={handleSync}>
          <RefreshCw size={13} className={syncing ? 'spin-animation' : ''} /> Refresh
        </button>
      </div>
      <div style={{ overflowX: 'auto' }}>
        <table className="products-table" style={{ minWidth: 750 }}>
          <thead>
            <tr><th>DATE</th><th>DESCRIPTION</th><th>AMOUNT</th><th>ACCOUNT</th><th>CATEGORY</th><th>STATUS</th></tr>
          </thead>
          <tbody>
            {MOCK_TRANSACTIONS.map(t => (
              <tr key={t.id}>
                <td>{t.date}</td>
                <td style={{ maxWidth: 260 }}>{t.name}</td>
                <td style={{ fontWeight: 700, color: t.amount < 0 ? '#ef4444' : '#10b981' }}>
                  {t.amount < 0 ? '-' : '+'}${Math.abs(t.amount).toFixed(2)}
                </td>
                <td>{t.account}</td>
                <td><span style={{ background: '#f1f5f9', padding: '0.15rem 0.5rem', borderRadius: 6, fontSize: '0.75rem' }}>{t.category}</span></td>
                <td>{badge(t.status)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  /* ── Logs Tab ──────────────────────────────────────────────── */
  const renderLogs = () => (
    <div style={{ background: 'var(--etpos-card)', borderRadius: 10, border: '1px solid var(--etpos-border)', overflow: 'hidden' }}>
      <div style={{ padding: '1.25rem 1.5rem', borderBottom: '1px solid var(--etpos-border)' }}>
        <h3 style={{ margin: 0, fontSize: '1.05rem', fontWeight: 700 }}>Activity Logs</h3>
      </div>
      <div style={{ overflowX: 'auto' }}>
        <table className="products-table" style={{ minWidth: 800 }}>
          <thead>
            <tr><th>EVENT</th><th>ACCOUNT</th><th>STATUS</th><th>MESSAGE</th><th style={{ textAlign: 'right' }}>DATE & TIME</th></tr>
          </thead>
          <tbody>
            {MOCK_LOGS.map(l => (
              <tr key={l.id}>
                <td style={{ fontWeight: 600 }}>{l.event}</td>
                <td>{l.account}</td>
                <td>{badge(l.status)}</td>
                <td style={{ maxWidth: 350, whiteSpace: 'normal', lineHeight: 1.4 }}>{l.message}</td>
                <td style={{ color: 'var(--etpos-text-muted)', textAlign: 'right' }}>{l.date}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  return (
    <div className="module-container">
      {/* Header */}
      <div className="module-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <div style={{ background: '#0c2d6215', padding: '0.75rem', borderRadius: 12, color: '#0c2d62' }}>
            <svg width="28" height="28" viewBox="0 0 60 40" fill="none">
              <rect width="60" height="40" rx="6" fill="#0c2d62" />
              <text x="8" y="28" fontFamily="Arial" fontWeight="900" fontSize="20" fill="white">plaid</text>
            </svg>
          </div>
          <div>
            <h2>Plaid Bank Integration</h2>
            <p style={{ margin: 0, fontSize: '0.875rem', color: 'var(--etpos-text-muted)' }}>
              Connect US bank accounts · Pay by Bank · Auto-reconciliation
            </p>
          </div>
        </div>
        <div style={{ display: 'flex', gap: '0.75rem' }}>
          <a href="https://plaid.com/docs" target="_blank" rel="noreferrer" className="export-btn" style={{ textDecoration: 'none' }}>
            <ExternalLink size={14} /> Plaid Docs
          </a>
          <button className="add-btn" onClick={simulateLinkBank}>
            <Plus size={14} /> Link Bank Account
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="module-tabs">
        {tabs.map(t => (
          <button key={t.id} className={`tab-btn ${activeTab === t.id ? 'active' : ''}`}
            onClick={() => setActiveTab(t.id)}
            style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
            {t.icon} {t.label}
          </button>
        ))}
      </div>

      {/* Content */}
      <div className="module-content" style={{ padding: '0.5rem', background: 'transparent', border: 'none', boxShadow: 'none' }}>
        {activeTab === 'setup' && renderSetup()}
        {activeTab === 'accounts' && renderAccounts()}
        {activeTab === 'payments' && renderPayments()}
        {activeTab === 'transactions' && renderTransactions()}
        {activeTab === 'logs' && renderLogs()}
      </div>

      <style dangerouslySetInnerHTML={{
        __html: `
        .spin-animation { animation: plaid-spin 1.2s linear infinite; }
        @keyframes plaid-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
      `}} />
    </div>
  );
};

export default PlaidConnector;
